import { useEffect } from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { currentSubscription, toPayload } from './push';

const REGISTER = gql`
  mutation ReclaimPushSubscription($input: PushSubscriptionInput!) {
    registerPushSubscription(input: $input) {
      thisBrowserRegistered
    }
  }
`;

/**
 * Re-claim this browser's push subscription for whoever is signed in NOW.
 *
 * A push subscription belongs to a BROWSER and outlives a session, so on a shared machine
 * the endpoint stays registered to whoever enabled it. Without this, person A signs out,
 * person B signs in, and A's notifications render on B's screen.
 *
 * The server's `ON CONFLICT (endpoint) DO UPDATE SET email = <caller>` moves the row to
 * the current user, so one idempotent call on boot closes that. It also refreshes
 * `last_seen_at`, which is the offboarding heartbeat the delivery queries filter on — a
 * departed staffer stops receiving because they can no longer authenticate to refresh it.
 *
 * No-ops for the overwhelming majority of users, who have no subscription at all.
 *
 * Residual, and unclosable from the client: a push that arrives after A walks away and
 * before B signs in. Sign-out tears the subscription down, which covers the normal path.
 */
export function usePushReconcile(email?: string): void {
  const [register] = useMutation(REGISTER);

  useEffect(() => {
    if (!email) return;
    let live = true;
    void (async () => {
      const sub = await currentSubscription().catch(() => null);
      if (!live || !sub) return;
      await register({ variables: { input: toPayload(sub) } }).catch(() => undefined);
    })();
    return () => {
      live = false;
    };
  }, [email, register]);
}
